import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-link.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/chemistry/elements/components/x-section.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import '@adornis/fonts/fonts.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { getCompanyInfo } from '@ils/config/project-config.js';
import { ExamType } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { map, of, switchMap } from 'rxjs';
import type { ILSUser } from '../../db/ils-user.js';
import { Order } from '../../db/order.js';
import '../components/ils-dashboard-order-item.js';

const orderSelectionSet = {
  _id: 1,
  createdAt: 1,
  revokedAt: 1,
  products: { product: { _id: 1, name: 1, reference: 1 } },
  invoices: { stripeID: 1, reference: 1 },
  status: 1,
  amount: 1,
} as const satisfies BaseQLSelectionSet<Order>;

type NarrowedOrder = NarrowedEntity<Order, typeof orderSelectionSet>;

interface ProductOrderGroup {
  productId: string;
  productName: string;
  orders: NarrowedOrder[];
}

/**
 * @element ils-dashboard
 */
@customElement('ils-dashboard')
export class IlsDashboard extends ChemistryLitElement {
  @state() private _isOrderExpanded: Maybe<string>;
  @state() private _showRevokedOrders = false;
  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());
  @state() private readonly _orders = new RXController(
    this,
    this._currentUser.observable,
    user$ =>
      user$.pipe(
        switchMap(user =>
          user
            ? Order.getByUserID(user._id)(orderSelectionSet).then(a => a as unknown as NarrowedOrder[])
            : of([] as NarrowedOrder[]),
        ),
        map(orders => orders.sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis())),
      ),
    { pipeIsLoading: true },
  );

  private _groupOrdersByProduct(orders: NarrowedOrder[]): ProductOrderGroup[] {
    const groupMap = new Map<string, ProductOrderGroup>();

    orders.forEach(order => {
      const product = order.products[0]?.product;
      const productID = product?.reference;
      if (!productID) return;
      if (!groupMap.has(productID)) {
        groupMap.set(productID, {
          productId: productID,
          productName: product.name,
          orders: [],
        });
      }
      groupMap.get(productID)?.orders.push(order);
    });

    return Array.from(groupMap.values());
  }

  override render() {
    return html`
      <x-section ${css({ padding: `${this.spacing.lg} 0` })}>
        <x-flex horizontal wrap space="lg" padding="sm"> ${this._renderOrderList()} </x-flex>
      </x-section>
    `;
  }

  private _renderOrderList(): Renderable {
    if (this._orders.isLoading) return html` <ils-loader></ils-loader> `;

    const orderGroups = this._groupOrdersByProduct(this._orders.value ?? []);

    return html`
      <x-flex space="md" flex ${css({ minWidth: '300px' })}>
        <x-flex horizontal space-between space="md" crossaxis-center>
          <x-h3> Buchungen </x-h3>
          ${this._renderRevokedToggleButton()}
        </x-flex>
        <x-flex space="sm">
          ${orderGroups.length
            ? html` ${repeat(orderGroups, group => this._renderProductGroup(group))} `
            : html`
                <x-empty-placeholder .text=${`Noch keine Prüfungen gekauft`} .img=${'close'}></x-empty-placeholder>
              `}
          <x-link href=${getCompanyInfo(ExamType.FAMED).signupUrl}>
            <x-basic-card
              padding="md"
              space="md"
              horizontal
              crossaxis-center
              ${css({ cursor: 'pointer', width: '100%' })}
            >
              <x-button mode="icon" icon="shopping_cart"></x-button>
              <x-flex space="sm">
                <x-h4> Hier Prüfung buchen </x-h4>
                <x-text> Hier können Sie weitere Prüfungen buchen </x-text>
              </x-flex>
            </x-basic-card>
          </x-link>
        </x-flex>
      </x-flex>
    `;
  }

  private _renderProductGroup(group: ProductOrderGroup): Renderable {
    const visibleOrders = group.orders.filter(order => this._showRevokedOrders || order.status !== 'revoked');
    if (visibleOrders.length === 0) return nothing;

    return html`
      <ils-dashboard-order-item
        .productGroup=${{ ...group, orders: visibleOrders }}
        ?expanded=${this._isOrderExpanded === visibleOrders[0]?._id}
        @click-order-item=${(e: CustomEvent<{ orderId: string }>) => this._adjustExpandedOrderItemID(e.detail.orderId)}
      ></ils-dashboard-order-item>
    `;
  }

  private _renderRevokedToggleButton(): Renderable {
    return html`
      <x-toggle-button
        label="Stornierte Buchungen anzeigen"
        .value=${this._showRevokedOrders}
        @value-changed=${(e: ValueEvent<boolean>) => (this._showRevokedOrders = e.detail.value)}
      ></x-toggle-button>
    `;
  }

  private _adjustExpandedOrderItemID(id: string) {
    this._isOrderExpanded = this._isOrderExpanded !== id ? id : null;
  }
}
